import type { JSX, ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';

import { CONTROL_TYPE } from '../../utils/constants';
import Control from '../Control';

interface WrapperProps {
  isDashboard?: boolean;
  isHelp?: boolean;
}

const Wrapper = styled.header<WrapperProps>`
  width: 100%;
  margin-bottom: ${({ isDashboard, isHelp }) =>
    !isDashboard && !isHelp ? '24px' : '0'};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: bold;
`;

const TitleWrapper = styled.div`
  width: 100%;

  @media only screen and (max-width: 700px) {
    width: 100%;
  }
`;

interface TitleProps {
  isDashboard?: boolean;
  isTracker?: boolean;
}

const Title = styled.h2<TitleProps>`
  font-weight: 700 !important;
  font-size: 32px;
  line-height: 42px;
  color: #012912;
  font-family: 'Helvetica Now For Cargill', Helvetica, Arial, sans-serif;
  letter-spacing: 0;
  margin-bottom: ${({ isDashboard, isTracker }) => {
    if (isDashboard) return '0';
    if (isTracker) return '8px';
    return '16px';
  }};
  margin-right: ${({ isTracker }) =>
    isTracker ? '16px' : '0'};

  @media only screen and (max-width: 700px) {
    font-size: 28px;
    line-height: 36px;
  }
`;

const SubTitle = styled.h4`
  font: var(--sprout-font-body1);
  color: #101c16;
`;

interface FormImageProps {
  isTracker?: boolean;
}

const FormImage = styled.img<FormImageProps>`
  width: ${({ isTracker }) =>
    isTracker ? '181px' : '130px'};
  aspect-ratio: ${({ isTracker }) =>
    isTracker ? '181 / 82' : '130 / 120'};

  @media only screen and (max-width: 700px) {
    display: none;
  }
`;


interface FormHeaderProps {
  cardId?: string | number;
  title?: string;
  subtitle?: React.ReactNode;
  image?: ReactNode;
}


function FormHeader({
  title,
  subtitle,
  image
}: FormHeaderProps): JSX.Element | null {
  const pathname = window.location.pathname || '';
  const isDashboard = pathname.includes('/dashboard');
  const isHelp = pathname.includes('/help');
  const isTracker = pathname.includes('/tracker');
  const { t } = useTranslation();

  if (!isHelp && !isDashboard && !subtitle) {
    return null;
  }

  const radioButtons = {
    name: 'type',
    label: '',
    type: CONTROL_TYPE.radio,
    disableAutoSave: true,
    options: [
      {
        label: t('support.contact.technicalQuestion.label'),
        value: 'technicalQuestion'
      },
      {
        label: t('support.contact.contentQuestion.label'),
        value: 'contentQuestion'
      }
    ]
  };

  return (
    <Wrapper isDashboard={isDashboard} isHelp={isHelp}>
      <Header>
        <TitleWrapper>
          {(isDashboard || isHelp || isTracker) && (
            <Title
              isDashboard={isDashboard}
              isTracker={isTracker}
            >
              {title}
            </Title>
          )}

          {(subtitle || isHelp) && (
            <SubTitle>{subtitle || ''}</SubTitle>
          )}

          {isHelp && <Control {...radioButtons} />}
        </TitleWrapper>

        {(isHelp || isTracker) && image && (
          typeof image === 'string' ? (
            <FormImage
              isTracker={isTracker}
              src={image}
              alt={`form-img-${title}`}
            />
          ) : (
            image
          )
        )}
      </Header>
    </Wrapper>
  );
}

export default FormHeader;